import React, { useState } from "react";
import { Mail, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Visible, click-to-copy contact email row. Surfaces alongside any "Book a
 * setup call" mailto CTA so users without a configured local mail client
 * (Gmail/web, mobile browser, etc.) can still grab the address easily.
 *
 * Props:
 *   - email:    address to display (required)
 *   - variant:  "light" (white-ish text on dark bg) | "dark" (dark on light)
 *   - subject:  optional mailto subject for the underlying link
 */
export default function ContactEmailBlock({
  email,
  variant = "dark",
  subject = "",
  className = "",
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      toast.success("Email copied to clipboard", { description: email });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API can be blocked on iOS Safari in some contexts —
      // fall back to a selection-style toast so the address is still grabbable.
      toast.message("Press and hold to copy:", { description: email });
    }
  }

  const subjectQs = subject ? `?subject=${encodeURIComponent(subject)}` : "";
  const isLight = variant === "light";
  const containerCls = isLight
    ? "border-slate-700 bg-slate-900/40 text-slate-100"
    : "border-slate-200 bg-white text-slate-900";
  const linkCls = isLight
    ? "text-amber-300 hover:text-amber-200"
    : "text-amber-700 hover:text-amber-800";
  const btnCls = isLight
    ? "border-slate-600 bg-slate-800/60 text-slate-100 hover:bg-slate-700"
    : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100";

  return (
    <div
      data-testid="contact-email-block"
      className={`inline-flex flex-wrap items-center justify-center gap-2 rounded-full border px-3 py-2 text-sm shadow-sm ${containerCls} ${className}`}
    >
      <Mail size={14} className={isLight ? "text-amber-300" : "text-amber-700"} />
      <a
        href={`mailto:${email}${subjectQs}`}
        data-testid="contact-email-link"
        className={`font-medium underline-offset-4 hover:underline ${linkCls}`}
      >
        {email}
      </a>
      <button
        type="button"
        data-testid="contact-email-copy-btn"
        onClick={copy}
        aria-label={`Copy ${email} to clipboard`}
        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${btnCls}`}
      >
        {copied ? (
          <>
            <Check size={12} /> Copied
          </>
        ) : (
          <>
            <Copy size={12} /> Copy
          </>
        )}
      </button>
    </div>
  );
}
